import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { format, formatDistanceToNow } from "date-fns";
import { useAuth } from "../../context/AuthContext";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { ScrollArea } from "../../components/ui/scroll-area";
import { Progress } from "../../components/ui/progress";
import {
  Users,
  FileText,
  Clock,
  CheckCircle,
  AlertCircle,
  Calendar,
  PlusCircle,
  ArrowRight,
  AlertTriangle,
  Activity,
  Zap,
  Target,
  Bell,
  X,
  Check,
} from "lucide-react";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Health Status Badge
const HealthBadge = ({ health }) => {
  const colors = {
    GREEN: "bg-[#d8e5b8] text-[#3d4f22] border-[#b8cf88]",
    AMBER: "bg-amber-100 text-amber-700 border-amber-200",
    RED: "bg-red-100 text-red-700 border-red-200",
  };

  const icons = {
    GREEN: <CheckCircle className="w-3 h-3" />,
    AMBER: <AlertTriangle className="w-3 h-3" />,
    RED: <AlertCircle className="w-3 h-3" />,
  };

  return (
    <div
      className={`inline-flex items-center gap-1 px-2 py-1 rounded-full border text-xs font-medium ${colors[health] || colors.GREEN}`}
    >
      {icons[health]}
      {health}
    </div>
  );
};

// Notification Item Component
const NotificationItem = ({ notification, onMarkRead }) => {
  const getNotificationIcon = (type) => {
    switch (type) {
      case "post_approved":
        return <CheckCircle className="w-5 h-5 text-[#556B2F]" />;
      case "post_rejected":
        return <X className="w-5 h-5 text-red-500" />;
      case "change_requested":
        return <AlertTriangle className="w-5 h-5 text-orange-500" />;
      case "new_comment":
        return <FileText className="w-5 h-5 text-[#556B2F]" />;
      case "password_reset_request":
        return <AlertCircle className="w-5 h-5 text-amber-500" />;
      default:
        return <Bell className="w-5 h-5 text-slate-500" />;
    }
  };

  return (
    <div
      className={`flex items-start gap-3 p-3 rounded-lg transition-all ${
        notification.read
          ? "bg-slate-50 dark:bg-slate-800/50"
          : "bg-[#f0f4e8] dark:bg-[#556B2F]/10 border-l-4 border-[#556B2F]"
      }`}
    >
      {getNotificationIcon(notification.type)}
      <div className="flex-1 min-w-0">
        <p
          className={`text-sm ${notification.read ? "text-slate-600 dark:text-slate-400" : "text-slate-900 dark:text-white font-medium"}`}
        >
          {notification.title}
        </p>
        <p className="text-xs text-slate-500 mt-1">{notification.message}</p>
        <p className="text-xs text-slate-400 mt-1">
          {formatDistanceToNow(new Date(notification.created_at), {
            addSuffix: true,
          })}
        </p>
      </div>
      {!notification.read && (
        <Button
          size="sm"
          variant="ghost"
          onClick={() => onMarkRead(notification.id)}
          className="text-[#556B2F] hover:text-[#3d4f22] dark:text-[#b8cf88] dark:hover:text-white"
        >
          <Check className="w-4 h-4" />
        </Button>
      )}
    </div>
  );
};

const AgencyDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [clients, setClients] = useState([]);
  const [recentPosts, setRecentPosts] = useState([]);
  const [notifications, setNotifications] = useState([]);

  // Agency Overview Stats
  const [overviewStats, setOverviewStats] = useState(null);
  const [clientHealthData, setClientHealthData] = useState([]);

  useEffect(() => {
    fetchDashboard();
    fetchAgencyOverview();
    fetchNotifications();
  }, []);

  const fetchDashboard = async () => {
    try {
      const response = await axios.get(`${API_URL}/dashboard/agency`);
      setStats(response.data.stats);
      setClients(response.data.clients);
      setRecentPosts(response.data.recent_posts);
    } catch (error) {
      console.error("Failed to fetch dashboard:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAgencyOverview = async () => {
    try {
      const response = await axios.get(`${API_URL}/agency/dashboard-stats`);
      setOverviewStats(response.data.totals);
      setClientHealthData(response.data.clients);
    } catch (error) {
      console.error("Failed to fetch agency overview:", error);
    }
  };

  const fetchNotifications = async () => {
    try {
      const response = await axios.get(`${API_URL}/notifications`);
      setNotifications(response.data.notifications || []);
    } catch (error) {
      console.error("Failed to fetch notifications:", error);
    }
  };

  const markNotificationRead = async (notificationId) => {
    try {
      await axios.put(`${API_URL}/notifications/${notificationId}/read`);
      setNotifications(
        notifications.map((n) =>
          n.id === notificationId ? { ...n, read: true } : n,
        ),
      );
    } catch (error) {
      console.error("Failed to mark notification as read:", error);
    }
  };

  const markAllRead = async () => {
    try {
      await axios.put(`${API_URL}/notifications/read-all`);
      setNotifications(notifications.map((n) => ({ ...n, read: true })));
    } catch (error) {
      console.error("Failed to mark all as read:", error);
    }
  };

  const getStatusBadgeClass = (status) => {
    const classes = {
      draft: "status-draft",
      internal_review: "bg-[#d8e5b8] text-[#3d4f22]",
      client_review: "bg-amber-100 text-amber-700",
      pending_approval: "status-pending",
      approved: "status-approved",
      rejected: "status-rejected",
      change_requested: "status-change-requested",
      scheduled: "status-scheduled",
      posted: "status-posted",
    };
    return classes[status] || "status-draft";
  };

  const formatStatus = (status) => {
    return status?.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());
  };

  const unreadCount = notifications.filter((n) => !n.read).length;

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#556B2F]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-in" data-testid="agency-dashboard">
      {/* Welcome Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 dark:text-white tracking-tight">
            Welcome back, {user?.name?.split(" ")[0]}
          </h1>
          <p className="text-slate-600 dark:text-slate-400 mt-1">
            Here's your agency overview for today.
          </p>
        </div>
        <Button
          onClick={() => navigate("/agency/posts/new")}
          className="bg-[#556B2F] hover:bg-[#3d4f22] text-white"
          data-testid="create-post-btn"
        >
          <PlusCircle className="w-4 h-4 mr-2" />
          Create Post
        </Button>
      </div>

      {/* Notifications Banner */}
      {unreadCount > 0 && (
        <Card className="bg-gradient-to-r from-[#f0f4e8] to-[#f0f4e8] dark:from-[#556B2F]/10 dark:to-[#3d4f22]/10 border-[#b8cf88] dark:border-[#3d4f22]">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-[#556B2F] rounded-full flex items-center justify-center">
                  <Bell className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="font-medium text-slate-900 dark:text-white">
                    You have {unreadCount} new notification
                    {unreadCount > 1 ? "s" : ""}
                  </p>
                  <p className="text-sm text-slate-600 dark:text-slate-400">
                    Check recent client actions and updates
                  </p>
                </div>
              </div>
              <Button variant="outline" size="sm" onClick={markAllRead}>
                Mark all read
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Recent Notifications (Top Section) */}
      {notifications.length > 0 && (
        <Card className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-lg font-semibold flex items-center gap-2">
              <Bell className="w-5 h-5 text-[#556B2F]" />
              Recent Activity
              {unreadCount > 0 && (
                <Badge className="bg-[#556B2F] text-white">{unreadCount}</Badge>
              )}
            </CardTitle>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate("/agency/notifications")}
            >
              View All <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </CardHeader>
          <CardContent>
            <ScrollArea className="max-h-64">
              <div className="space-y-2">
                {notifications.slice(0, 5).map((notification) => (
                  <NotificationItem
                    key={notification.id}
                    notification={notification}
                    onMarkRead={markNotificationRead}
                  />
                ))}
              </div>
            </ScrollArea>
          </CardContent>
        </Card>
      )}

      {/* Overview Stats */}
      {overviewStats && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="bg-gradient-to-br from-[#f0f4e8] to-[#f0f4e8] dark:from-[#556B2F]/10 dark:to-[#3d4f22]/10 border-[#b8cf88] dark:border-[#3d4f22]">
            <CardContent className="p-5">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-[#556B2F] rounded-xl flex items-center justify-center">
                  <FileText className="w-6 h-6 text-white" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900 dark:text-white">
                    {overviewStats.posts_this_month}
                  </p>
                  <p className="text-sm text-slate-500">Posts This Month</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-[#f0f4e8] to-[#f0f4e8] dark:from-[#556B2F]/10 dark:to-[#3d4f22]/10 border-[#b8cf88] dark:border-[#3d4f22]">
            <CardContent className="p-5">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-[#556B2F] rounded-xl flex items-center justify-center">
                  <Target className="w-6 h-6 text-white" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900 dark:text-white">
                    {overviewStats.avg_approval_rate}%
                  </p>
                  <p className="text-sm text-slate-500">Approval Rate</p>
                </div>
              </div>
              <Progress
                value={overviewStats.avg_approval_rate}
                className="mt-3 h-2"
              />
            </CardContent>
          </Card>

          <Card
            className={`bg-gradient-to-br ${overviewStats.overdue > 0 ? "from-red-50 to-orange-50 border-red-200" : "from-slate-50 to-gray-50 border-slate-200"} dark:from-[#1e2711]/20 dark:to-gray-900/20`}
          >
            <CardContent className="p-5">
              <div className="flex items-center gap-4">
                <div
                  className={`w-12 h-12 ${overviewStats.overdue > 0 ? "bg-red-500" : "bg-slate-400"} rounded-xl flex items-center justify-center`}
                >
                  <AlertTriangle className="w-6 h-6 text-white" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900 dark:text-white">
                    {overviewStats.overdue}
                  </p>
                  <p className="text-sm text-slate-500">Overdue Reviews</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-purple-50 to-violet-50 dark:from-purple-900/20 dark:to-violet-900/20 border-purple-200 dark:border-purple-800">
            <CardContent className="p-5">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-purple-500 rounded-xl flex items-center justify-center">
                  <Calendar className="w-6 h-6 text-white" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900 dark:text-white">
                    {overviewStats.scheduled_next_7}
                  </p>
                  <p className="text-sm text-slate-500">Scheduled (7 days)</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Quick Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800">
          <CardContent className="p-5">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-[#d8e5b8] dark:bg-[#556B2F]/15 rounded-xl flex items-center justify-center">
                <Users className="w-6 h-6 text-[#556B2F] dark:text-[#b8cf88]" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900 dark:text-white">
                  {stats?.total_clients || 0}
                </p>
                <p className="text-sm text-slate-500">Active Clients</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800">
          <CardContent className="p-5">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-amber-100 dark:bg-amber-900/30 rounded-xl flex items-center justify-center">
                <Clock className="w-6 h-6 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900 dark:text-white">
                  {stats?.pending_approval || 0}
                </p>
                <p className="text-sm text-slate-500">Pending Approval</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800">
          <CardContent className="p-5">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-[#d8e5b8] dark:bg-[#556B2F]/15 rounded-xl flex items-center justify-center">
                <CheckCircle className="w-6 h-6 text-[#3d4f22] dark:text-[#b8cf88]" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900 dark:text-white">
                  {stats?.approved || 0}
                </p>
                <p className="text-sm text-slate-500">Approved</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800">
          <CardContent className="p-5">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-purple-100 dark:bg-purple-900/30 rounded-xl flex items-center justify-center">
                <Calendar className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900 dark:text-white">
                  {stats?.scheduled || 0}
                </p>
                <p className="text-sm text-slate-500">Scheduled</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Alerts */}
      {(stats?.change_requested > 0 || overviewStats?.overdue > 0) && (
        <div className="grid md:grid-cols-2 gap-4">
          {stats?.change_requested > 0 && (
            <Card className="bg-orange-50 dark:bg-orange-900/20 border-orange-200 dark:border-orange-800">
              <CardContent className="p-4 flex items-center gap-4">
                <AlertCircle className="w-5 h-5 text-orange-600" />
                <div className="flex-1">
                  <p className="font-medium text-orange-900 dark:text-orange-100">
                    {stats.change_requested} post(s) need revisions
                  </p>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate("/agency/calendar")}
                >
                  View <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </CardContent>
            </Card>
          )}

          {overviewStats?.overdue > 0 && (
            <Card className="bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800">
              <CardContent className="p-4 flex items-center gap-4">
                <AlertTriangle className="w-5 h-5 text-red-600" />
                <div className="flex-1">
                  <p className="font-medium text-red-900 dark:text-red-100">
                    {overviewStats.overdue} post(s) are overdue for review
                  </p>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate("/agency/calendar")}
                >
                  View <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Main Content Grid */}
      <div className="grid lg:grid-cols-3 gap-6">
        {/* Client Health Overview */}
        <Card className="lg:col-span-2 bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800">
          <CardHeader className="flex flex-row items-center justify-between pb-4">
            <CardTitle className="text-lg font-semibold flex items-center gap-2">
              <Activity className="w-5 h-5 text-[#556B2F]" />
              Client Health Overview
            </CardTitle>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate("/agency/clients")}
              data-testid="view-all-clients"
            >
              View All <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </CardHeader>
          <CardContent>
            {clientHealthData.length === 0 ? (
              <div className="text-center py-12">
                <Users className="w-12 h-12 text-slate-300 dark:text-slate-600 mx-auto mb-4" />
                <h3 className="font-medium text-slate-900 dark:text-white mb-2">
                  No clients yet
                </h3>
                <p className="text-sm text-slate-500 mb-4">
                  Add your first client to start creating content
                </p>
                <Button
                  onClick={() => navigate("/agency/clients")}
                  className="bg-[#556B2F] hover:bg-[#3d4f22] text-white"
                >
                  <PlusCircle className="w-4 h-4 mr-2" />
                  Add Client
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                {clientHealthData.slice(0, 6).map((client) => (
                  <Link
                    key={client.client_id}
                    to={`/agency/clients/${client.client_id}`}
                    className="flex items-center gap-4 p-4 rounded-xl border border-slate-100 dark:border-slate-800 hover:border-[#b8cf88] dark:hover:border-[#3d4f22] hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-all"
                    data-testid={`client-health-${client.client_id}`}
                  >
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#556B2F] to-[#6B8E23] flex items-center justify-center text-white font-semibold text-lg">
                      {client.avatar_url ? (
                        <img
                          src={client.avatar_url}
                          alt=""
                          className="w-full h-full rounded-xl object-cover"
                        />
                      ) : (
                        client.name?.charAt(0)?.toUpperCase() || "C"
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h3 className="font-medium text-slate-900 dark:text-white truncate">
                          {client.name}
                        </h3>
                        {/* <HealthBadge health={client.health} /> */}
                      </div>
                      <p className="text-sm text-slate-500 truncate">
                        {client.last_activity
                          ? `Last active ${formatDistanceToNow(new Date(client.last_activity), { addSuffix: true })}`
                          : "No recent activity"}
                      </p>
                    </div>
                    <div className="flex items-center gap-4 text-sm">
                      <div className="text-center">
                        <p className="font-semibold text-slate-900 dark:text-white">
                          {client.posts_this_month}
                        </p>
                        <p className="text-xs text-slate-500">Posts</p>
                      </div>
                      <div className="text-center">
                        <p
                          className={`font-semibold ${
                            client.approval_rate >= 75
                              ? "text-[#556B2F] dark:text-[#b8cf88]"
                              : client.approval_rate >= 50
                                ? "text-amber-600 dark:text-amber-400"
                                : "text-red-600 dark:text-red-400"
                          }`}
                        >
                          {client.approval_rate}%
                        </p>
                        <p className="text-xs text-slate-500">Approved</p>
                      </div>
                      <div className="text-center">
                        <p className="font-semibold text-amber-600 dark:text-amber-400">
                          {client.pending_approvals}
                        </p>
                        <p className="text-xs text-slate-500">Pending</p>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Recent Posts */}
        <Card className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800">
          <CardHeader className="pb-4">
            <CardTitle className="text-lg font-semibold flex items-center gap-2">
              <Zap className="w-5 h-5 text-amber-500" />
              Recent Posts
            </CardTitle>
          </CardHeader>
          <CardContent>
            {recentPosts.length === 0 ? (
              <div className="text-center py-8">
                <FileText className="w-10 h-10 text-slate-300 dark:text-slate-600 mx-auto mb-3" />
                <p className="text-sm text-slate-500">No recent posts</p>
              </div>
            ) : (
              <ScrollArea className="h-[400px]">
                <div className="space-y-4">
                  {recentPosts.slice(0, 10).map((post) => (
                    <Link
                      key={post.id}
                      to={`/agency/posts/${post.id}`}
                      className="block p-3 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors border border-transparent hover:border-slate-200 dark:hover:border-slate-700"
                    >
                      <div className="flex items-start gap-3">
                        <div
                          className={`w-2 h-2 rounded-full mt-2 ${
                            post.status === "approved" ||
                            post.approval_stage === "approved"
                              ? "bg-[#556B2F]"
                              : post.status === "change_requested" ||
                                  post.approval_stage === "change_requested"
                                ? "bg-orange-500"
                                : post.status === "rejected"
                                  ? "bg-red-500"
                                  : "bg-[#556B2F]"
                          }`}
                        ></div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-slate-900 dark:text-white line-clamp-2">
                            {post.copy?.substring(0, 80)}...
                          </p>
                          <div className="flex items-center gap-2 mt-2">
                            <Badge
                              variant="outline"
                              className={`text-xs ${getStatusBadgeClass(post.approval_stage || post.status)}`}
                            >
                              {formatStatus(post.approval_stage || post.status)}
                            </Badge>
                          </div>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              </ScrollArea>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AgencyDashboard;
